import React, { useEffect, useState } from 'react';
import { analyticsAPI } from '../../lib/api';
import { TrendingUp, TrendingDown, Award, Target, DollarSign, Percent } from 'lucide-react';

const StatBox = ({ label, value, sub, icon: Ico, positive }) => (
  <div className="p-5 rounded-xl border border-[#1a2823] bg-[#0a1210]">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs text-gray-400 uppercase tracking-wider">{label}</span>
      <div className="w-8 h-8 rounded-lg bg-[#00e676]/10 border border-[#00e676]/20 flex items-center justify-center text-[#00e676]">
        <Ico size={14}/>
      </div>
    </div>
    <div className={`text-2xl font-extrabold mono ${positive === false ? 'text-red-400' : (positive ? 'text-[#00e676]' : 'text-white')}`}>{value}</div>
    {sub && <div className="mt-1 text-xs text-gray-400">{sub}</div>}
  </div>
);

const EquityChart = ({ data }) => {
  if (!data?.length) return <div className="h-56 flex items-center justify-center text-sm text-gray-500">No trade data yet</div>;
  const w = 900, h = 220, pad = 20;
  const vals = data.map(d => d.value);
  const min = Math.min(0, ...vals);
  const max = Math.max(0, ...vals);
  const range = max - min || 1;
  const points = data.map((d, i) => {
    const x = pad + (i / Math.max(1, data.length - 1)) * (w - 2 * pad);
    const y = h - pad - ((d.value - min) / range) * (h - 2 * pad);
    return `${x},${y}`;
  }).join(' ');
  const zeroY = h - pad - ((0 - min) / range) * (h - 2 * pad);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-56" preserveAspectRatio="none">
      <defs>
        <linearGradient id="eqCurveGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#00e676" stopOpacity="0.35"/>
          <stop offset="100%" stopColor="#00e676" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <line x1={pad} x2={w - pad} y1={zeroY} y2={zeroY} stroke="#141f1b" strokeDasharray="3 3"/>
      <polyline points={points} fill="none" stroke="#00e676" strokeWidth="2"/>
      <polygon points={`${points} ${w - pad},${h - pad} ${pad},${h - pad}`} fill="url(#eqCurveGrad)"/>
    </svg>
  );
};

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsAPI.get().then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-gray-500">Loading analytics…</div>;
  if (!data) return <div className="text-sm text-gray-500">Failed to load analytics.</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Analytics</h1>
        <p className="text-sm text-gray-400">Deep performance insights from your closed trades.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatBox label="Realised P&L" value={`${data.realised_pnl >= 0 ? '+' : ''}$${data.realised_pnl.toFixed(2)}`} positive={data.realised_pnl >= 0} icon={DollarSign} sub={`${data.total_trades} trades`}/>
        <StatBox label="Win Rate" value={`${data.win_rate.toFixed(1)}%`} icon={Percent} sub={`${data.wins}W / ${data.losses}L`}/>
        <StatBox label="Profit Factor" value={data.profit_factor.toFixed(2)} icon={Target} sub={data.profit_factor >= 1.5 ? 'Excellent' : (data.profit_factor >= 1 ? 'Profitable' : 'Underwater')}/>
        <StatBox label="Avg Win / Loss" value={`+$${data.avg_win.toFixed(2)}`} sub={`Avg loss $${data.avg_loss.toFixed(2)}`} icon={Award}/>
      </div>

      <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-bold">Cumulative P&L</h3>
            <p className="text-xs text-gray-400">Realised profit over time</p>
          </div>
          <span className={`text-lg font-extrabold mono ${data.realised_pnl >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>{data.realised_pnl >= 0 ? '+' : ''}${data.realised_pnl.toFixed(2)}</span>
        </div>
        <EquityChart data={data.equity_curve}/>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
          <h3 className="text-white font-bold mb-4">Highs & Lows</h3>
          <div className="space-y-3">
            <Row label="Gross Profit" value={`+$${data.gross_profit.toFixed(2)}`} positive/>
            <Row label="Gross Loss" value={`-$${data.gross_loss.toFixed(2)}`} negative/>
            <Row label="Largest Win" value={`+$${data.largest_win.toFixed(2)}`} positive/>
            <Row label="Largest Loss" value={`$${data.largest_loss.toFixed(2)}`} negative/>
          </div>
        </div>
        <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
          <h3 className="text-white font-bold mb-4">Performance by Symbol</h3>
          {data.symbol_breakdown.length === 0 && <div className="text-sm text-gray-500">No data yet.</div>}
          <div className="space-y-2">
            {data.symbol_breakdown.map(s => (
              <div key={s.symbol} className="flex items-center justify-between py-2 border-b border-[#141f1b] last:border-0">
                <div>
                  <div className="text-sm text-white font-semibold">{s.symbol}</div>
                  <div className="text-[10px] text-gray-500">{s.trades} trades · {s.wins} wins ({((s.wins / s.trades) * 100).toFixed(0)}%)</div>
                </div>
                <span className={`text-sm mono font-semibold ${s.pnl >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>{s.pnl >= 0 ? '+' : ''}${s.pnl.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, positive, negative }) => (
  <div className="flex items-center justify-between py-2 border-b border-[#141f1b] last:border-0">
    <span className="text-sm text-gray-400">{label}</span>
    <span className={`text-sm mono font-semibold ${positive ? 'text-[#00e676]' : (negative ? 'text-red-400' : 'text-white')}`}>{value}</span>
  </div>
);

export default Analytics;
